import {ArrowHelper, Group, type Material, type Mesh, type Sprite, Vector3,} from 'three';
import {ViewportGizmoLabelTextureFactory} from './ViewportGizmoLabelTextureFactory';


/** 跟随中央方向立方体旋转的 XYZ 坐标轴指示器。 */
export class ViewportGizmoAxisIndicator extends Group {
  /** XYZ 轴组相对于参考实现基础尺寸的统一显示倍率。 */
  public static readonly DISPLAY_SCALE = 1.5;

  /** 坐标轴在局部方向上离开立方体中心的起点距离。 */
  private static readonly AXIS_START_DISTANCE = 20;

  /** 坐标轴箭头端点离开立方体中心的局部距离。 */
  private static readonly AXIS_END_DISTANCE = 34;

  /** 坐标轴标签距离。 */
  private static readonly LABEL_DISTANCE = 38.5;

  /** 当前指示器拥有的标签 Sprite。 */
  private readonly labels: readonly Sprite[];

  /**
   * 创建红 X、绿 Y、蓝 Z 三根坐标轴。
   *
   * @param labelTextureFactory 坐标轴标签纹理工厂。
   */
  public constructor(
    labelTextureFactory: ViewportGizmoLabelTextureFactory,
  ) {
    super();
    this.name = '视口方向坐标轴指示器';

    // 在组根节点统一缩放，确保轴线、箭头、标签距离和文字保持原始比例。
    this.scale.setScalar(ViewportGizmoAxisIndicator.DISPLAY_SCALE);

    const axes = [
      this.createAxis(new Vector3(1, 0, 0), '#ff0000'),
      this.createAxis(new Vector3(0, 1, 0), '#00c853'),
      this.createAxis(new Vector3(0, 0, 1), '#0066ff'),
    ] as const;
    this.labels = [
      this.createLabel(labelTextureFactory, 'X', '#ff0000', new Vector3(1, 0, 0)),
      this.createLabel(labelTextureFactory, 'Y', '#00c853', new Vector3(0, 1, 0)),
      this.createLabel(labelTextureFactory, 'Z', '#0066ff', new Vector3(0, 0, 1)),
    ];

    this.add(...axes, ...this.labels);
  }

  /** 释放 ArrowHelper 几何、材质和文字材质。 */
  public dispose(): void {
    this.traverse((object) => {
      const candidate = object as Partial<Mesh>;
      candidate.geometry?.dispose();

      const material = candidate.material;
      if (Array.isArray(material)) {
        for (const item of material) {
          item.dispose();
        }
      } else {
        (material as Material | undefined)?.dispose();
      }
    });
    this.clear();
  }

  /**
   * 创建单根不参与深度遮挡的坐标轴箭头。
   *
   * @param direction 坐标轴单位方向。
   * @param color 坐标轴显示颜色。
   * @returns 已配置渲染层级的箭头。
   */
  private createAxis(direction: Vector3, color: string): ArrowHelper {
    const origin = direction.clone().multiplyScalar(
      ViewportGizmoAxisIndicator.AXIS_START_DISTANCE,
    );
    const visibleLength = (
      ViewportGizmoAxisIndicator.AXIS_END_DISTANCE
      - ViewportGizmoAxisIndicator.AXIS_START_DISTANCE
    );

    // 轴只绘制立方体外侧区间，既保留原终点位置，又让轴与主面之间形成留白。
    const arrow = new ArrowHelper(
      direction,
      origin,
      visibleLength,
      color,
      7,
      4,
    );
    arrow.renderOrder = 70;
    arrow.traverse((object) => {
      const material = (object as Partial<Mesh>).material;
      if (Array.isArray(material)) {
        for (const item of material) {
          item.depthTest = false;
          item.depthWrite = false;
        }
      } else if (material) {
        material.depthTest = false;
        material.depthWrite = false;
      }
    });
    return arrow;
  }

  /**
   * 创建并定位单个坐标轴标签。
   *
   * @param factory 标签纹理工厂。
   * @param text 坐标轴名称。
   * @param color 坐标轴颜色。
   * @param direction 标签所在轴方向。
   * @returns 已定位的标签 Sprite。
   */
  private createLabel(
    factory: ViewportGizmoLabelTextureFactory,
    text: string,
    color: string,
    direction: Vector3,
  ): Sprite {
    const label = factory.createAxisLabel(text, color);
    label.position.copy(
      direction.multiplyScalar(ViewportGizmoAxisIndicator.LABEL_DISTANCE),
    );
    return label;
  }
}

